/**
 * usePayment Hook
 * ahooks data fetching hook for vendor payment data
 *
 * @see api-spec.yaml - Vendor Payment section
 */

import { useRequest, clearCache } from 'ahooks';
import type { Options } from 'ahooks/lib/useRequest/src/types';
import { paymentApi } from '@/services/api';
import { usePaymentStore, usePaymentDataForVendor } from '@/stores';
import type { VendorPaymentDataResponse } from '@/types/api/paymentTerm';

/**
 * Helper to invalidate payment cache for a vendor
 */
const invalidatePaymentCache = (vendorId: number) => {
  clearCache(`/api/vendors/${vendorId}/payment`);
};

/**
 * Hook to fetch payment data for a specific vendor
 */
export function usePayment(
  vendorId: number | null,
  config?: Options<VendorPaymentDataResponse, []>
) {
  const { setPaymentDataForVendor, setLoading, setError } = usePaymentStore();

  // Fetch payment data with ahooks
  const { data, error, loading, refresh } = useRequest(
    () => paymentApi.get(vendorId!),
    {
      ready: !!vendorId,
      cacheKey: vendorId ? `/api/vendors/${vendorId}/payment` : undefined,
      staleTime: 30000, // 30 seconds
      ...config,
      onSuccess: (data) => {
        if (vendorId) {
          setPaymentDataForVendor(vendorId, data);
          setLoading(false);
          setError(null);
        }
        config?.onSuccess?.(data, []);
      },
      onError: (err) => {
        setLoading(false);
        setError(err.message || 'Failed to fetch payment data');
        config?.onError?.(err, []);
      },
    }
  );

  // Get payment data from store for immediate access
  // Always call hooks unconditionally (React Rules of Hooks)
  // Use 0 as fallback when vendorId is null (will return null from store)
  const paymentDataFromStore = usePaymentDataForVendor(vendorId ?? 0);
  const paymentData = vendorId ? paymentDataFromStore : null;

  return {
    paymentData: data ?? paymentData,
    error,
    isLoading: loading,
    mutate: refresh,
    refresh,
  };
}

/**
 * Hook for payment data mutations (create, update)
 * All mutations invalidate ahooks cache to ensure UI stays in sync
 */
export function usePaymentMutations(vendorId: number) {
  const { setPaymentDataForVendor } = usePaymentStore();

  const createPaymentData = async (
    data: Parameters<typeof paymentApi.create>[1]
  ) => {
    const response = await paymentApi.create(vendorId, data);
    setPaymentDataForVendor(vendorId, response);
    // Invalidate cache to refresh payment data
    invalidatePaymentCache(vendorId);
    return response;
  };

  const updatePaymentData = async (
    data: Parameters<typeof paymentApi.update>[1]
  ) => {
    const response = await paymentApi.update(vendorId, data);
    setPaymentDataForVendor(vendorId, response);
    // Invalidate cache to refresh payment data
    invalidatePaymentCache(vendorId);
    return response;
  };

  return {
    createPaymentData,
    updatePaymentData,
  };
}

/**
 * Hook to check if payment data exists for a vendor
 */
export function useHasPaymentData(vendorId: number | null) {
  // Always call hooks unconditionally (React Rules of Hooks)
  // Use 0 as fallback when vendorId is null (will return null from store)
  const paymentDataFromStore = usePaymentDataForVendor(vendorId ?? 0);
  const paymentData = vendorId ? paymentDataFromStore : null;
  return !!paymentData;
}
